/**
 * İstemci tarafı document meta / canonical / JSON-LD (CSR; tam indeks için prerender önerilir).
 * `REACT_APP_SITE_URL` — üretim kökü, örn. https://demaxtore.com (sonunda / olmasın).
 */

const SITE_NAME = 'DeMaxtore';

function envSiteUrl() {
  return String(process.env.REACT_APP_SITE_URL || '')
    .trim()
    .replace(/\/+$/, '');
}

/** Tam URL üretimi (SSR yokken window öncelikli). */
export function getSiteOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin && /^https?:/i.test(window.location.origin)) {
    return window.location.origin.replace(/\/+$/, '');
  }
  return envSiteUrl();
}

export function absoluteUrl(pathOrUrl) {
  const s = String(pathOrUrl || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  const origin = getSiteOrigin();
  if (!origin) return s;
  const path = s.startsWith('/') ? s : `/${s}`;
  return `${origin}${path}`.replace(/([^:]\/)\/+/g, '$1');
}

function ensureMeta(attrName, attrValue) {
  const sel = `meta[${attrName}="${attrValue.replace(/"/g, '\\"')}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  return el;
}

function setMetaContent(attrName, attrValue, content) {
  const el = ensureMeta(attrName, attrValue);
  if (content == null || content === '') {
    el.removeAttribute('content');
    return;
  }
  el.setAttribute('content', String(content));
}

function ensureLinkRel(rel) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  return el;
}

function removeJsonLdById(id) {
  const el = document.getElementById(id);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

/**
 * @param {object} opts
 * @param {string} [opts.title] — tarayıcı sekmesi (SITE_NAME ile birleştirilir)
 * @param {string} [opts.description]
 * @param {string} [opts.canonicalPath] — kök-relative, örn. /products/wc-1
 * @param {string} [opts.ogImage] — mutlak veya kök-relative görsel
 * @param {boolean} [opts.noindex]
 * @param {object|object[]} [opts.jsonLd] — script[type=application/ld+json] olarak enjekte
 * @param {string} [opts.jsonLdId] — default 'page-jsonld'
 */
export function applyPageSeo(opts = {}) {
  if (typeof document === 'undefined') return;

  const {
    title,
    description,
    canonicalPath,
    ogImage,
    noindex,
    jsonLd,
    jsonLdId = 'page-jsonld',
  } = opts;

  const baseTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — B2B Export Platform`;
  document.title = baseTitle.slice(0, 200);

  setMetaContent('name', 'description', (description || '').slice(0, 320));

  const robots = noindex ? 'noindex, nofollow' : 'index, follow';
  setMetaContent('name', 'robots', robots);

  const origin = getSiteOrigin();
  const canonical =
    canonicalPath && origin
      ? absoluteUrl(canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`)
      : origin || '';

  if (canonical) {
    const link = ensureLinkRel('canonical');
    link.setAttribute('href', canonical);
  }

  const ogTitle = title || SITE_NAME;
  setMetaContent('property', 'og:title', ogTitle.slice(0, 200));
  setMetaContent('property', 'og:description', (description || '').slice(0, 300));
  setMetaContent('property', 'og:type', 'website');
  setMetaContent('property', 'og:site_name', SITE_NAME);
  if (canonical) setMetaContent('property', 'og:url', canonical);

  const ogImgAbs = ogImage ? absoluteUrl(ogImage) : '';
  if (ogImgAbs) {
    setMetaContent('property', 'og:image', ogImgAbs);
    setMetaContent('property', 'og:image:alt', ogTitle.slice(0, 120));
  }

  setMetaContent('name', 'twitter:card', ogImgAbs ? 'summary_large_image' : 'summary');
  setMetaContent('name', 'twitter:title', ogTitle.slice(0, 70));
  setMetaContent('name', 'twitter:description', (description || '').slice(0, 200));
  if (ogImgAbs) setMetaContent('name', 'twitter:image', ogImgAbs);

  removeJsonLdById(jsonLdId);
  if (jsonLd) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = jsonLdId;
    const payload = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    script.textContent = JSON.stringify(payload.length === 1 ? payload[0] : payload);
    document.head.appendChild(script);
  }
}

export function truncatePlain(text, maxLen) {
  const t = String(text || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(0, maxLen - 1)).trim()}…`;
}

/** Organization + WebSite şeması (liste ve statik sayfalar). */
export function buildOrganizationJsonLd() {
  const origin = getSiteOrigin();
  if (!origin) return null;
  const logoPath = String(process.env.REACT_APP_SEO_DEFAULT_OG_IMAGE || '').trim();
  const logoUrl = logoPath ? absoluteUrl(logoPath) : '';
  const org = {
    '@type': 'Organization',
    name: SITE_NAME,
    url: origin,
  };
  if (logoUrl) org.logo = logoUrl;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      org,
      {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: origin,
      },
    ],
  };
}
